import React, { ErrorInfo, ReactNode, Suspense } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';

interface LazyRouteProps {
  children: ReactNode;
  loadingLabel: string;
  errorLabel: string;
  retryLabel: string;
}

interface LazyRouteState {
  failed: boolean;
}

class RouteErrorBoundary extends React.Component<LazyRouteProps, LazyRouteState> {
  state: LazyRouteState = { failed: false };

  static getDerivedStateFromError(): LazyRouteState {
    return { failed: true };
  }

  componentDidCatch(_error: Error, _errorInfo: ErrorInfo): void {
    // The fallback intentionally avoids exposing chunk-loader details to users.
  }

  render(): ReactNode {
    if (this.state.failed)
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => this.setState({ failed: false })}>
              {this.props.retryLabel}
            </Button>
          }
        >
          {this.props.errorLabel}
        </Alert>
      );
    return this.props.children;
  }
}

const LazyRoute = ({ children, loadingLabel, errorLabel, retryLabel }: LazyRouteProps) => (
  <RouteErrorBoundary loadingLabel={loadingLabel} errorLabel={errorLabel} retryLabel={retryLabel}>
    <Suspense
      fallback={
        <Box display="flex" justifyContent="center" p={4} role="status" aria-label={loadingLabel}>
          <CircularProgress />
        </Box>
      }
    >
      {children}
    </Suspense>
  </RouteErrorBoundary>
);

export default LazyRoute;
